import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ApService } from 'src/app/shared/services/ap.service';

interface SupportInterface {
  name: string;
  label: string;
  address: string;
  link: string;
  type: string;
  state: boolean;
}

interface DiagnosticsInterfaceRow {
  id: string;
  zone: string;
  state: 'up' | 'down';
  sdwan: string;
  type: string;
  network: string;
  device: string;
  protocol: string;
  mac: string;
  address: string;
  external_ip: string;
  isp: string;
  action: string;
  uptime_seconds: number;
  usage_bytes: number;
}

interface SupportTool {
  id: string;
  name: string;
  description: string;
  category: string;
  kind: 'diagnostic' | 'service' | 'configuration';
  risk_level: string;
  read_only: boolean;
  disruptive: boolean;
  expected_duration_seconds: number;
  expected_downtime: string;
  required_parameters: string[];
  data_usage_warning: boolean;
  available: boolean;
  backend_action?: string;
  selected?: boolean;
  technicalOpen?: boolean;
}

interface SupportOperation {
  id: string;
  parameters: any;
}

interface LteCarrierOption {
  value: string;
  label: string;
  helper?: string;
  logo: string;
}

interface LteCarrierMarket {
  country?: string;
  country_code?: string;
  carriers?: Array<{ value?: string; label?: string }>;
  current_carrier?: { value?: string; label?: string };
}

interface LteDeviceDetail {
  label: string;
  value: string;
}

interface LanJumpDestination {
  ip: string;
  hostname: string;
  mac: string;
  interfaceName: string;
  is_sip_phone?: boolean;
  sip_user_id?: string;
  sip_name?: string;
  sip_status?: string;
}

interface LanJumpLiveState {
  enabled: boolean;
  status: string;
  appliedAtMs: number;
  expiration: number;
}

interface WorkflowPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  toolIds: string[];
  symptoms: string[];
}

interface RejectedCommand {
  line: number;
  command: string;
  reason: string;
}

interface CliOutputSegment {
  text: string;
  url?: string;
}

interface RunHistoryItem {
  startedAt: Date;
  label: string;
  status: string;
  runtimeMs: number;
  output: string;
  diagnosticOutput: boolean;
}

interface HealthMetric {
  label: string;
  value: string;
  state: 'healthy' | 'warning' | 'failed' | 'unknown';
  detail: string;
  diagnosticId: string;
}

const MODEM_FIRMWARE_MEMORY_COMMAND = `awk '/^MemAvailable:/ { mb=int($2/1024); printf "AVAILABLE_MB=%d\\nREQUIRED_MB=110\\nSTATUS=%s\\n", mb, (mb >= 110 ? "READY" : "INSUFFICIENT") }' /proc/meminfo`;

function formatCliOutput(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  }

  const output = String(value);
  const trimmed = normalizeTerminalJsonCandidate(output);
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return output;
  const candidates = [trimmed];
  if (trimmed.indexOf('\n') >= 0) candidates.push(trimmed.replace(/\n/g, ''));
  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === 'object') return JSON.stringify(parsed, null, 2);
    } catch (error) {
      continue;
    }
  }
  return output;
}

function normalizeTerminalJsonCandidate(value: string): string {
  return String(value || '')
    .replace(/\u001B\][^\u0007]*(?:\u0007|\u001B\\)/g, '')
    .replace(/\u001B(?:[@-Z\\-_]|\[[0-?]*[ -/]*[@-~])/g, '')
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .trim();
}

function stripBenignSdwanWarnings(value: string): string {
  return String(value || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter(line => !/^\s*RTNETLINK answers:\s*Invalid argument\s*$/i.test(line))
    .join('\n')
    .trim();
}

function linkifyCliOutput(value: string): CliOutputSegment[] {
  const output = String(value || '');
  const segments: CliOutputSegment[] = [];
  const urlPattern = /https?:\/\/[^\s<>"'`]+/gi;
  let cursor = 0;
  let match: RegExpExecArray;
  while ((match = urlPattern.exec(output)) !== null) {
    if (match.index > cursor) segments.push({ text: output.slice(cursor, match.index) });
    const matchedValue = match[0];
    const url = matchedValue.replace(/[),.;:\]}]+$/g, '');
    const trailingText = matchedValue.slice(url.length);
    if (url) segments.push({ text: url, url });
    if (trailingText) segments.push({ text: trailingText });
    cursor = match.index + matchedValue.length;
  }
  if (cursor < output.length || !segments.length) segments.push({ text: output.slice(cursor) });
  return segments;
}

function parseArpDestinations(value: any): LanJumpDestination[] {
  const destinations: LanJumpDestination[] = [];
  const seen = new Set<string>();
  normalizeTerminalJsonCandidate(value).split('\n').forEach(line => {
    const busybox = line.match(/^\s*(?:(\S+)\s+)?\(((?:\d{1,3}\.){3}\d{1,3})\)\s+at\s+([0-9a-f:]{17})\b.*?\s+on\s+(\S+)\s*$/i);
    const linux = line.match(/^\s*((?:\d{1,3}\.){3}\d{1,3})\s+\S+\s+\S+\s+([0-9a-f:]{17})\s+\S+\s+(\S+)\s*$/i);
    const enriched = line.match(/^\s*((?:\d{1,3}\.){3}\d{1,3})\s+([0-9a-f:]{17})\s+(\S+)\s*$/i);
    const ip = busybox ? busybox[2] : linux ? linux[1] : enriched && enriched[1];
    const mac = busybox ? busybox[3] : linux ? linux[2] : enriched && enriched[2];
    const interfaceName = busybox ? busybox[4] : linux ? linux[3] : '';
    const discoveredHostname = enriched && enriched[3] !== 'Unknown' ? enriched[3] : '';
    if (!ip || !mac || seen.has(ip)) return;
    const parts = ip.split('.');
    if (!parts.every(part => Number(part) <= 255)) return;
    seen.add(ip);
    destinations.push({
      ip,
      hostname: discoveredHostname || (busybox && busybox[1] && busybox[1] !== '?' ? busybox[1] : ''),
      mac: mac.toUpperCase(),
      interfaceName
    });
  });
  return destinations.sort((left, right) => left.ip.localeCompare(right.ip, undefined, { numeric: true }));
}

function enrichLanJumpDestinations(
  liveDestinations: LanJumpDestination[],
  enrichment: LanJumpDestination[]
): LanJumpDestination[] {
  const byIp = new Map<string, LanJumpDestination>();
  const byMac = new Map<string, LanJumpDestination>();
  enrichment.forEach(item => {
    if (item && item.ip) byIp.set(item.ip, item);
    if (item && item.mac) byMac.set(item.mac.toUpperCase(), item);
  });

  return liveDestinations.map(live => {
    const database = byIp.get(live.ip) || byMac.get(String(live.mac || '').toUpperCase());
    return database ? {
      ...database,
      ip: live.ip,
      mac: live.mac || database.mac,
      interfaceName: live.interfaceName,
      hostname: database.hostname || live.hostname
    } : live;
  });
}

function parseLanJumpLiveState(value: any, slot: number): LanJumpLiveState | null {
  const prefix = `autossh.landevice${slot}.`;
  const values: any = {};
  normalizeTerminalJsonCandidate(value).split('\n').forEach(line => {
    const trimmed = line.trim();
    if (trimmed.indexOf(prefix) !== 0) return;
    const separator = trimmed.indexOf('=');
    if (separator < 0) return;
    const key = trimmed.slice(prefix.length, separator);
    values[key] = trimmed.slice(separator + 1).trim().replace(/^(['"])(.*)\1$/, '$2');
  });
  if (!Object.keys(values).length) return null;
  const appliedAtSeconds = Number(values.applied_time);
  const expiration = Number(values.expire_time);
  const status = String(values.status || '').toLowerCase();
  const enabled = String(values.enabled || '0') === '1' && status !== 'inactive';
  return {
    enabled,
    status: enabled ? 'active' : 'inactive',
    appliedAtMs: Number.isFinite(appliedAtSeconds) && appliedAtSeconds > 0 ? appliedAtSeconds * 1000 : 0,
    expiration: Number.isFinite(expiration) && expiration > 0 ? expiration : 0
  };
}

function stripLanJumpLiveState(value: any, slot: number): string {
  const prefix = `autossh.landevice${slot}`;
  return normalizeTerminalJsonCandidate(value).split('\n')
    .filter(line => line.trim().indexOf(prefix) !== 0)
    .join('\n')
    .trim();
}

function formatDiagnosticCliOutput(value: any): string {
  const normalized = normalizeTerminalJsonCandidate(typeof value === 'string' ? value : formatCliOutput(value));
  let expectingSpeedTestPostData = false;
  const output = normalized.split('\n').filter(line => {
    const trimmed = line.trim();
    if (/^post-data\s*:/i.test(trimmed)) {
      expectingSpeedTestPostData = !trimmed.replace(/^post-data\s*:/i, '').trim();
      return false;
    }
    if (expectingSpeedTestPostData) {
      expectingSpeedTestPostData = false;
      if (/^(?:recommendedserverid|serverid)=\d+(?:&[a-z][a-z0-9_]*=[^&\s]*)+$/i.test(trimmed)) return false;
    }
    if (/^__RRT_(?:ORCH|COMMAND|OUTPUT)[A-Z0-9_:._-]*$/i.test(trimmed)) return false;
    return !/^(?:\[[^\]]+\]\s*)?(?:[^\s@]+@)?[^\s:]+:[^#\n]*#(?:\s+.*)?$/.test(trimmed);
  }).join('\n').replace(/^\s*\n/, '').trim();
  return formatCliOutput(output);
}

const WORKFLOWS: WorkflowPreset[] = [
  {
    id: 'internet-unavailable',
    name: 'Internet unavailable',
    description: 'Check links, routes, DNS and end-to-end reachability.',
    icon: 'cloud_off',
    toolIds: ['interface_statistics', 'link_statistics', 'routing_table', 'ping', 'dns_lookup', 'mtr', 'link_flaps', 'system_state'],
    symptoms: ['No browsing', 'DNS failures', 'All WAN paths unavailable']
  },
  {
    id: 'intermittent',
    name: 'Intermittent connectivity',
    description: 'Correlate link flaps, loss, latency and routes.',
    icon: 'sync_problem',
    toolIds: ['link_statistics', 'link_flaps', 'ping', 'mtr', 'routing_table'],
    symptoms: ['Brief outages', 'Calls dropping', 'Frequent failover']
  },
  {
    id: 'latency-loss',
    name: 'High latency or packet loss',
    description: 'Measure path health and current link quality.',
    icon: 'network_check',
    toolIds: ['link_statistics', 'ping', 'mtr', 'speed_test'],
    symptoms: ['Slow applications', 'Poor voice quality', 'Packet loss']
  },
  {
    id: 'cellular',
    name: '4GLTE backup issue',
    description: 'Inspect modem, carrier, interface and backup performance.',
    icon: 'signal_cellular_alt',
    toolIds: ['cellular_statistics', 'modem_version', 'system_state', 'carrier_scan', 'speed_test', 'link_flaps'],
    symptoms: ['Backup does not connect', 'High cellular latency', 'SIM or carrier issue']
  },
  {
    id: 'routing',
    name: 'Routing issue',
    description: 'Inspect active routes, neighbours and target reachability.',
    icon: 'alt_route',
    toolIds: ['routing_table', 'ipv4_neighbours', 'ping', 'mtr'],
    symptoms: ['One network unreachable', 'Wrong gateway', 'Duplicate default route']
  },
  {
    id: 'performance',
    name: 'Device performance issue',
    description: 'Check system resources, QoS and interface throughput.',
    icon: 'memory',
    toolIds: ['system_state', 'qos_statistics', 'interface_statistics', 'link_statistics'],
    symptoms: ['Slow UI', 'High load', 'Traffic shaping concern']
  }
];

@Component({
  selector: 'app-device-command-center',
  templateUrl: './device-command-center.component.html',
  styleUrls: ['./device-command-center.component.scss']
})
export class DeviceCommandCenterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() device: any[] = [];
  @Input() userRole: number;

  get canUseAdvancedCommands(): boolean {
    return this.userRole === 0;
  }

  get advancedActions(): SupportTool[] {
    return this.canUseAdvancedCommands
      ? this.tools.filter(tool => ['download_archive', 'simulate_failover'].indexOf(tool.id) >= 0)
      : [];
  }
  @ViewChild('commandReviewDialog') commandReviewDialog: TemplateRef<any>;
  @ViewChild('mtrInputDialog') mtrInputDialog: TemplateRef<any>;
  @ViewChild('speedTestInputDialog') speedTestInputDialog: TemplateRef<any>;
  @ViewChild('ifstatInputDialog') ifstatInputDialog: TemplateRef<any>;

  workflows = WORKFLOWS;
  selectedWorkflow: WorkflowPreset = WORKFLOWS[0];
  healthMetrics: HealthMetric[] = [];
  tools: SupportTool[] = [];
  supportInterfaces: SupportInterface[] = [];
  diagnosticsRows: DiagnosticsInterfaceRow[] = [];
  diagnosticsTotalUsageBytes = 0;
  diagnosticsCapturedAt: string = null;
  diagnosticsSnapshotStale = true;
  catalogRefreshing = false;
  diagnosticGroups: Array<{ name: string; tools: SupportTool[] }> = [];
  lowImpactActions: SupportTool[] = [];
  highImpactActions: SupportTool[] = [];
  networkRoutineActions: SupportTool[] = [];
  networkHighImpactActions: SupportTool[] = [];
  configurationTools: SupportTool[] = [];
  lteConfigurationTools: SupportTool[] = [];
  lanJumpTool: SupportTool;
  lanJumpTestTool: SupportTool;
  lanJumpDiscoveryTool: SupportTool;
  lteCarrierScanTool: SupportTool;
  lteSimReadTool: SupportTool;
  lteModemVersionTool: SupportTool;
  lteFirmwareMemoryTool: SupportTool;
  lteModemOffTool: SupportTool;
  lteSimUnloadTool: SupportTool;
  lteModemResetTool: SupportTool;
  lteSimLoadTool: SupportTool;
  lteCarrierConnectTool: SupportTool;
  lteCarrierOptions: LteCarrierOption[] = [
    { value: 'auto', label: 'Auto', logo: '' },
    { value: 'verizon', label: 'Verizon', logo: 'assets/icons/carriers/verizon.svg' },
    { value: 'tmobile', label: 'T-Mobile', logo: 'assets/icons/carriers/tmobile.svg' },
    { value: 'att', label: 'AT&T', logo: 'assets/icons/carriers/att.svg' }
  ];
  lteCarrierCountryLabel = 'United States';
  lteCarrierCountryCode = 'US';
  currentLteCarrierLabel = 'Not reported';
  currentLteCarrierValue = '';
  selectedLteCarrier = '';
  selectedDiagnostics: SupportTool[] = [];
  selectedDiagnosticCount = 0;
  selectedEstimatedDuration = 0;
  speedTestSelected = false;
  mtrSelected = false;
  catalogLoading = true;
  catalogError = '';
  diagnosticSearch = '';
  diagnosticsTarget = '8.8.8.8';
  selectedInterface = '';
  speedMode = 'both';
  speedConnectionType = 'auto';
  speedShare = true;
  mtrCount = 3;
  mtrInterval = 1;
  mtrResolveNames = true;
  ifstatGroup: 'sdwan' | 'lan' = 'sdwan';
  ifstatDuration = 5;
  configurationValues: any = {
    lte_safeguard: false,
    carrier_switch: false
  };
  currentConfigurationValues: any = {
    lte_safeguard: false,
    carrier_switch: false
  };
  lanJumpSessions: any[] = [
    { description: 'LANDevice1', protocol: 'https', port: 443, target: '', expiration: 120, slot: 1,
      status: 'inactive', enabled: false, appliedAtMs: 0 },
    { description: 'LANDevice2', protocol: 'https', port: 443, target: '', expiration: 15, slot: 2,
      status: 'inactive', enabled: false, appliedAtMs: 0 }
  ];
  lanJump = this.lanJumpSessions[0];
  lanJumpTestState: 'idle' | 'testing' | 'passed' | 'failed' = 'idle';
  lanJumpTestMessage = 'Test the destination before enabling temporary access.';
  lanJumpTestOutput = '';
  lanJumpTestSignature = '';
  lanJumpDestinations: LanJumpDestination[] = [];
  lanJumpDiscoverySource: 'hybrid' | 'shell' = 'shell';
  lanJumpEnrichment: LanJumpDestination[] = [];
  lanJumpDiscoveryState: 'idle' | 'loading' | 'loaded' | 'failed' = 'idle';
  lanJumpDiscoveryMessage = 'Discover devices recently seen on the LAN, or enter an IPv4 address manually.';

  rawCommands = '';
  validCommands: string[] = [];
  rejectedCommands: RejectedCommand[] = [];
  reviewCommands: string[] = [];
  reviewRejected: RejectedCommand[] = [];
  reviewTitle = '';
  reviewType: 'support' | 'advanced' = 'support';
  reviewOperations: SupportOperation[] = [];
  reviewDurationSeconds = 0;
  reviewRisk = 'read-only';
  reviewLoading = false;
  reviewError = '';

  running = false;
  polling = false;
  elapsedSeconds = 0;
  executionState: 'idle' | 'running' | 'completed' | 'accepted' | 'failed' = 'idle';
  output = '';
  responseMeta: any = null;
  diagnosticOutput = false;
  activeRunExpectedSeconds = 0;
  activeTab = 0;
  outputTabIndex = -1;
  suppressExecutionOutput = false;
  readonly showTroubleshootTab = false;
  readonly showServiceActionsTab = false;
  readonly showConfigurationTab = false;
  readonly showDiagnosticsTelemetry = false;
  readonly showAdditionalDiagnostics = false;
  readonly showLanJumpTab = true;
  runHistory: RunHistoryItem[] = [];

  private executionSubscription: Subscription;
  private statusSubscription: Subscription;
  private catalogSubscription: Subscription;
  private previewSubscription: Subscription;
  private lanJumpTestSubscription: Subscription;
  private lanJumpDiscoverySubscription: Subscription;
  private lanJumpCountdownTimer: any;
  private progressTimer: any;
  private statusPollTimer: any;
  private reviewDialogRef: MatDialogRef<any>;
  private parameterDialogRef: MatDialogRef<any>;
  private activeRunLabel = '';
  private activeRunStartedAt = 0;
  private activeSupportOperations: SupportOperation[] = [];
  private initialized = false;
  private catalogDeviceMac = '';

  constructor(private apService: ApService, private dialog: MatDialog, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.initialized = true;
    this.refreshHealthMetrics();
    this.loadCatalog();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.device) return;
    this.refreshHealthMetrics();
    if (!this.initialized || !this.deviceMac || this.deviceMac === this.catalogDeviceMac) return;
    this.loadCatalog();
  }

  get currentDevice(): any {
    return this.device && this.device[0] ? this.device[0] : {};
  }

  get deviceMac(): string {
    return this.currentDevice.mac || '';
  }

  get deviceName(): string {
    return this.currentDevice.name || this.deviceMac || 'Selected device';
  }

  get deviceModel(): string {
    return this.currentDevice.deviceMetaInfo && this.currentDevice.deviceMetaInfo.deviceModel ||
      this.currentDevice.model || 'Unknown model';
  }

  get firmwareVersion(): string {
    return this.currentDevice.statistics && this.currentDevice.statistics.firmware &&
      this.currentDevice.statistics.firmware.currentVersion || 'Unknown';
  }

  get deviceLocation(): string {
    return [this.currentDevice.location, this.currentDevice.state]
      .filter((value, index, list) => value && list.indexOf(value) === index).join(', ') || 'Not set';
  }

  get lteDeviceDetails(): LteDeviceDetail[] {
    const statistics = this.currentDevice.statistics && this.currentDevice.statistics['4glte'] || {};
    const configured = this.currentDevice.templateConfig && this.currentDevice.templateConfig['4glte'] &&
      this.currentDevice.templateConfig['4glte'].config || {};
    const readValue = (liveValue: any, configuredValue: any = ''): string => {
      const value = liveValue !== undefined && liveValue !== null && liveValue !== '' ? liveValue : configuredValue;
      return value !== undefined && value !== null && value !== '' ? String(value) : '—';
    };
    return [
      { label: 'APN', value: readValue(statistics.apn, configured.apn) },
      // { label: 'Auth type', value: readValue(statistics.auth, configured.auth) },
      // { label: 'User name', value: readValue(statistics.username, configured.username) },
      // { label: 'Password', value: password === '—' ? password : password.replace(/./g, '*') },
      { label: 'PDP type', value: readValue(statistics.pdp, configured.pdptype).replace(/-/g, ' ') },
      // { label: 'Service domain', value: readValue(statistics.servicedomain, configured.servicedomain) },
      // { label: 'Operator', value: readValue(statistics.operator) },
      { label: 'IMEI', value: readValue(statistics.imei) },
      { label: 'CCID', value: readValue(statistics.ccid) },
      { label: 'IMSI', value: readValue(statistics.imsi) },
      { label: 'ICCID', value: readValue(statistics.iccid) }
    ];
  }

  get lteApn(): string {
    const statistics = this.currentDevice.statistics && this.currentDevice.statistics['4glte'] || {};
    const configured = this.currentDevice.templateConfig && this.currentDevice.templateConfig['4glte'] &&
      this.currentDevice.templateConfig['4glte'].config || {};
    const apn = statistics.apn !== undefined && statistics.apn !== null && statistics.apn !== ''
      ? statistics.apn
      : configured.apn;
    return apn === undefined || apn === null ? '' : String(apn).trim();
  }

  get isRabbitRunMultiCarrierSim(): boolean {
    return /^rabbitrun2?$/i.test(this.lteApn);
  }

  get deviceOnline(): boolean {
    if (!this.currentDevice.lastSeen) return true;
    return Date.now() - new Date(this.currentDevice.lastSeen).getTime() < 5 * 60 * 1000;
  }

  get uptimeText(): string {
    const seconds = Number(this.currentDevice.upTime || this.currentDevice.statistics &&
      this.currentDevice.statistics.system && this.currentDevice.statistics.system.uptime || 0);
    if (!seconds) return 'Unknown';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return days ? `${days}d ${hours}h` : `${hours}h`;
  }

  get lastSeenText(): string {
    if (!this.currentDevice.lastSeen) return 'Unknown';
    const seconds = Math.max(0, Math.floor((Date.now() - new Date(this.currentDevice.lastSeen).getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  }

  private refreshHealthMetrics(): void {
    const links = this.deviceLinks;
    const linkMetric = (names: string[], label: string, diagnosticId: string): HealthMetric => {
      const link = links.find(item => names.indexOf(String(item.network || item.interface || item.ifname)) >= 0);
      if (!link) return { label, value: 'Not reported', state: 'unknown', detail: 'No current device data', diagnosticId };
      const up = link.state === true || link.up === true || link.up === 'true';
      const latency = Number(link.latency || 0);
      return {
        label,
        value: up ? (latency ? `${latency.toFixed(1)} ms` : 'Online') : 'Down',
        state: !up ? 'failed' : latency > 150 ? 'warning' : 'healthy',
        detail: `${link.ifname || link.interface || link.network}${link.loss !== undefined ? `, ${link.loss}% loss` : ''}`,
        diagnosticId
      };
    };
    const system = this.currentDevice.statistics && this.currentDevice.statistics.system || {};
    this.healthMetrics = [
      linkMetric(['wan'], 'WAN', 'link_statistics'),
      linkMetric(['wan2'], 'WAN2', 'link_statistics'),
      linkMetric(['4GLTE', 'wwan0'], '4GLTE', 'cellular_statistics'),
      linkMetric(['rrt1', 'wg1'], 'Overlay', 'overlay_reachability'),
      {
        label: 'System load',
        value: system.loadavg !== undefined ? String(system.loadavg) : 'Unknown',
        state: Number(system.loadavg) > 4 ? 'warning' : system.loadavg !== undefined ? 'healthy' : 'unknown',
        detail: `Memory ${system.mem_usage !== undefined ? system.mem_usage + '% used' : 'not reported'}`,
        diagnosticId: 'system_state'
      }
    ];
  }

  get deviceLinks(): any[] {
    const teams = this.currentDevice.statistics && this.currentDevice.statistics.teams || {};
    const teamName = Object.keys(teams)[0];
    return teamName && Array.isArray(teams[teamName].links) ? teams[teamName].links : [];
  }

  refreshDiagnosticGroups(): void {
    const search = this.diagnosticSearch.trim().toLowerCase();
    const groups: any = {};
    this.tools.filter(item => item.kind === 'diagnostic').forEach(item => {
      if (search && `${item.name} ${item.description} ${item.category}`.toLowerCase().indexOf(search) < 0) return;
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    this.diagnosticGroups = Object.keys(groups).map(name => ({ name, tools: groups[name] }));
  }

  get outputSegments(): CliOutputSegment[] {
    return linkifyCliOutput(this.displayOutput);
  }

  get displayOutput(): string {
    return this.diagnosticOutput ? formatDiagnosticCliOutput(this.output) : this.output;
  }

  get structuredResult(): any {
    const candidate = normalizeTerminalJsonCandidate(this.displayOutput);
    if (!candidate || (candidate[0] !== '{' && candidate[0] !== '[')) return null;
    try {
      const value = JSON.parse(candidate);
      return value && !Array.isArray(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  get speedResult(): any {
    const result = this.structuredResult;
    if (result && (result.download_mbit !== undefined || result.upload_mbit !== undefined)) return result;

    const values: any = {};
    this.displayOutput.split('\n').forEach(line => {
      const match = line.match(/^\s*([A-Z_]+)\s*=\s*(.*?)\s*$/i);
      if (!match) return;
      values[match[1].toUpperCase()] = match[2];
    });
    const download = this.speedNumber(values.DOWNLOAD_SPEED || values.DOWNLOAD_MBIT);
    const upload = this.speedNumber(values.UPLOAD_SPEED || values.UPLOAD_MBIT);
    if (download === null && upload === null) return null;
    return {
      ping: this.speedNumber(values.LATENCY || values.PING),
      jitter: this.speedNumber(values.JITTER),
      download_mbit: download,
      upload_mbit: upload,
      share: values.IMAGE_URL || values.SHARE || values.SHARE_URL || values.RESULT_URL || ''
    };
  }

  get modemMemoryResult(): any {
    const available = this.displayOutput.match(/^AVAILABLE_MB=(\d+)$/m);
    const required = this.displayOutput.match(/^REQUIRED_MB=(\d+)$/m);
    const status = this.displayOutput.match(/^STATUS=(READY|INSUFFICIENT)$/m);
    if (!available || !required || !status) return null;
    return {
      availableMb: Number(available[1]),
      requiredMb: Number(required[1]),
      ready: status[1] === 'READY'
    };
  }

  get speedResultShareUrl(): string {
    const result = this.speedResult;
    const share = result && (
      result.image_url ||
      result.imageUrl ||
      result.IMAGE_URL ||
      result.share ||
      result.share_url ||
      result.result_url
    );
    if (!share) return '';

    const url = String(share).match(/https?:\/\/[^\s\])]+/i);
    return url ? url[0].replace(/^http:\/\//i, 'https://') : '';
  }

  get diagnosticsUpCount(): number {
    return this.diagnosticsRows.filter(item => item.state === 'up').length;
  }

  get diagnosticsDownCount(): number {
    return this.diagnosticsRows.filter(item => item.state === 'down').length;
  }

  get executionStatusText(): string {
    if (this.executionState === 'running') {
      return this.elapsedSeconds < 8 ? 'Sending operations to device' : `Waiting for device output (${this.elapsedSeconds}s)`;
    }
    if (this.executionState === 'accepted') return this.polling ? 'Operation running; checking device status' : 'Operation accepted by device';
    if (this.executionState === 'completed') return 'Execution completed';
    if (this.executionState === 'failed') return 'Execution failed';
    return '';
  }

  get canProceedReview(): boolean {
    return !this.running;
  }

  get showReviewCommands(): boolean {
    return this.reviewType === 'advanced';
  }

  get reviewDisplayCommands(): string[] {
    if (this.reviewType !== 'support') return this.reviewCommands;

    return this.reviewCommands.map(command =>
      command === MODEM_FIRMWARE_MEMORY_COMMAND ? 'free -m' : command
    );
  }

  get diagnosticsTabIndex(): number {
    return this.showTroubleshootTab ? 1 : 0;
  }

  get runHistoryTabIndex(): number {
    return Number(this.showTroubleshootTab) + 3 + Number(this.showAdditionalDiagnostics) +
      Number(this.showServiceActionsTab) + Number(this.showConfigurationTab) +
      Number(this.showLanJumpTab) + Number(this.canUseAdvancedCommands);
  }

  get selectedDiagnosticsRequireTarget(): boolean {
    return this.selectedDiagnostics.some(tool => tool.required_parameters.indexOf('target') >= 0);
  }

  get selectedDiagnosticsRequireInterface(): boolean {
    return this.selectedDiagnostics.some(tool => tool.required_parameters.indexOf('interface') >= 0);
  }

  get diagnosticSelectionValid(): boolean {
    if (!this.selectedDiagnosticCount) return false;
    if (this.selectedDiagnosticsRequireTarget && !this.normalizedTarget()) return false;
    return !this.selectedDiagnosticsRequireInterface || Boolean(this.selectedInterface);
  }

  get selectedInterfaceDetails(): SupportInterface {
    return this.supportInterfaces.find(item => item.name === this.selectedInterface);
  }

  get ifstatGroupLabel(): string {
    return this.ifstatGroup === 'lan' ? 'LAN interfaces' : 'SD-WAN links';
  }

  get ifstatInterfaceLabels(): string[] {
    return this.supportInterfaces
      .filter(item => this.ifstatGroup === 'lan' ? this.isLanInterface(item) : this.isSdwanInterface(item))
      .map(item => item.label || item.name);
  }

  selectedToolsUsing(parameter: string): string {
    return this.selectedDiagnostics
      .filter(tool => tool.required_parameters.indexOf(parameter) >= 0)
      .map(tool => tool.name)
      .join(', ');
  }

  activateWorkflow(workflow: WorkflowPreset): void {
    this.selectedWorkflow = workflow;
    this.tools.filter(item => item.kind === 'diagnostic').forEach(item => item.selected = workflow.toolIds.indexOf(item.id) >= 0);
    this.refreshSelectionState();
  }

  selectHealthDiagnostic(metric: HealthMetric): void {
    const tool = this.tools.find(item => item.id === metric.diagnosticId);
    if (tool && tool.available) {
      tool.selected = true;
      this.refreshSelectionState();
      this.activeTab = this.diagnosticsTabIndex;
    }
  }

  clearDiagnostics(): void {
    this.tools.filter(item => item.kind === 'diagnostic').forEach(item => item.selected = false);
    this.refreshSelectionState();
  }

  retryCatalog(): void {
    this.catalogDeviceMac = '';
    this.loadCatalog();
  }

  refreshDiagnosticsSnapshot(): void {
    if (this.catalogRefreshing || this.catalogLoading) return;
    this.loadCatalog(true);
  }

  reviewDiagnosticShortcut(toolId: string, speedMode?: string): void {
    const tool = this.tools.find(item => item.id === toolId && item.kind === 'diagnostic');
    if (!tool || !tool.available || this.running || this.reviewLoading) return;
    if (speedMode) this.speedMode = speedMode;
    if (tool.required_parameters.indexOf('target') >= 0 && !this.normalizedTarget()) return;
    if (tool.required_parameters.indexOf('interface') >= 0 && !this.selectedInterface) return;
    this.executeDiagnosticOperations([this.operationForTool(tool)], tool.name, Number(tool.expected_duration_seconds || 0));
  }

  openMtrInputs(): void {
    if (!this.canOpenDiagnostic('mtr')) return;
    this.parameterDialogRef = this.dialog.open(this.mtrInputDialog, {
      width: '460px', maxWidth: '96vw', disableClose: true,
      panelClass: 'device-diagnostic-input-dialog'
    });
  }

  openSpeedTestInputs(): void {
    if (!this.canOpenDiagnostic('speed_test')) return;
    this.parameterDialogRef = this.dialog.open(this.speedTestInputDialog, {
      width: '500px', maxWidth: '96vw', disableClose: true,
      panelClass: 'device-diagnostic-input-dialog'
    });
  }

  openIfstatInputs(): void {
    if (!this.canOpenDiagnostic('interface_statistics')) return;
    this.parameterDialogRef = this.dialog.open(this.ifstatInputDialog, {
      width: '500px', maxWidth: '96vw', disableClose: true,
      panelClass: 'device-diagnostic-input-dialog'
    });
  }

  cancelParameterDialog(): void {
    if (this.parameterDialogRef) this.parameterDialogRef.close();
  }

  submitMtrInputs(): void {
    if (!this.selectedInterface || !this.normalizedTarget()) return;
    this.cancelParameterDialog();
    this.reviewDiagnosticShortcut('mtr');
  }

  submitSpeedTestInputs(): void {
    if (!this.selectedInterface) return;
    this.cancelParameterDialog();
    this.reviewDiagnosticShortcut('speed_test', this.speedMode);
  }

  submitIfstatInputs(): void {
    if (['sdwan', 'lan'].indexOf(this.ifstatGroup) < 0 || [5, 10].indexOf(Number(this.ifstatDuration)) < 0) return;
    const tool = this.tools.find(item => item.id === 'interface_statistics' && item.kind === 'diagnostic');
    if (!tool || !tool.available || this.running) return;
    this.cancelParameterDialog();
    this.executeDiagnosticOperations(
      [this.operationForTool(tool)],
      `Interface statistics (${this.ifstatGroupLabel}, ${this.ifstatDuration} seconds)`,
      Number(tool.expected_duration_seconds || this.ifstatDuration)
    );
  }

  setIfstatDuration(duration: number): void {
    if ([5, 10].indexOf(Number(duration)) >= 0) this.ifstatDuration = Number(duration);
  }

  setSpeedMode(mode: string): void {
    if (['both', 'download', 'upload'].indexOf(mode) >= 0) this.speedMode = mode;
  }

  canOpenDiagnostic(toolId: string): boolean {
    const tool = this.tools.find(item => item.id === toolId && item.kind === 'diagnostic');
    return Boolean(tool && tool.available && !this.running && !this.reviewLoading);
  }

  isDiagnosticAvailable(toolId: string): boolean {
    const tool = this.tools.find(item => item.id === toolId && item.kind === 'diagnostic');
    if (!tool || !tool.available) return false;
    if (tool.required_parameters.indexOf('interface') >= 0 && !this.selectedInterface) return false;
    return tool.required_parameters.indexOf('target') < 0 || Boolean(this.normalizedTarget());
  }

  formatBytes(value: number): string {
    const bytes = Number(value || 0);
    if (!bytes) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const amount = bytes / Math.pow(1024, index);
    return `${amount.toFixed(index < 2 ? 0 : 1)} ${units[index]}`;
  }

  formatUptime(value: number): string {
    let seconds = Math.max(0, Math.floor(Number(value || 0)));
    if (!seconds) return '0m';
    const days = Math.floor(seconds / 86400);
    seconds %= 86400;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return [days ? `${days}d` : '', hours ? `${hours}h` : '', `${minutes}m`].filter(Boolean).join(' ');
  }

  trackDiagnosticsRow(_index: number, row: DiagnosticsInterfaceRow): string {
    return row.id;
  }

  reviewWorkflow(): void {
    this.activateWorkflow(this.selectedWorkflow);
    this.reviewDiagnostics(`${this.selectedWorkflow.name} workflow`);
  }

  reviewDiagnostics(title = 'Review diagnostic run'): void {
    if (!this.diagnosticSelectionValid) return;
    this.executeDiagnosticOperations(
      this.selectedDiagnostics.map(item => this.operationForTool(item)),
      title === 'Review diagnostic run' ? 'Diagnostic run' : title,
      this.selectedEstimatedDuration
    );
  }

  private executeDiagnosticOperations(operations: SupportOperation[], title: string, expectedDurationSeconds: number): void {
    if (this.running || !operations.length) return;
    this.reviewDurationSeconds = expectedDurationSeconds;
    this.executeSupportOperations({ operations }, title);
  }

  reviewTool(tool: SupportTool): void {
    if (!tool || !tool.available) return;
    this.previewSupportOperations([this.operationForTool(tool)], `Review ${tool.name}`);
  }

  runLteUtility(tool: SupportTool): void {
    if (!tool || !tool.available || this.running || this.reviewLoading) return;
    if (['lte_modem_off', 'lte_sim_unload'].indexOf(tool.id) >= 0) {
      this.reviewTool(tool);
      return;
    }
    this.reviewDurationSeconds = Number(tool.expected_duration_seconds || 0);
    this.executeSupportOperations({ operations: [this.operationForTool(tool)] }, tool.name);
  }

  reviewConfiguration(tool: SupportTool): void {
    this.reviewTool(tool);
  }

  selectLteCarrier(carrier: string): void {
    if (this.running || this.reviewLoading) return;
    if (!this.lteCarrierOptions.some(option => option.value === carrier)) return;
    this.selectedLteCarrier = carrier;
  }

  applyCarrierMarket(market: LteCarrierMarket): void {
    const carriers = Array.isArray(market && market.carriers)
      ? market.carriers
          .filter(carrier => carrier && carrier.value && carrier.label)
          .map(carrier => ({
            value: this.normalizeCarrierValue(carrier.value),
            label: String(carrier.label),
            logo: this.carrierLogo(String(carrier.value))
          }))
      : [];

    if (!carriers.length) return;
    this.lteCarrierCountryLabel = String(market.country || 'United States');
    this.lteCarrierCountryCode = String(market.country_code || 'US').toUpperCase();
    const currentCarrierValue = market.current_carrier && market.current_carrier.value
      ? this.normalizeCarrierValue(market.current_carrier.value)
      : '';
    this.currentLteCarrierValue = currentCarrierValue;
    this.currentLteCarrierLabel = market.current_carrier && market.current_carrier.label
      ? String(market.current_carrier.label)
      : 'Not reported';
    if (!carriers.some(carrier => carrier.value === 'auto')) {
      carriers.unshift({ value: 'auto', label: 'Auto', logo: '' });
    }
    this.lteCarrierOptions = carriers;
    if (!carriers.some(carrier => carrier.value === this.selectedLteCarrier)) {
      this.selectedLteCarrier = carriers.some(carrier => carrier.value === currentCarrierValue)
        ? currentCarrierValue
        : '';
    }
  }

  private carrierLogo(carrier: string): string {
    const supportedLogos: any = {
      verizon: 'verizon',
      tmobile: 'tmobile',
      att: 'att',
      rogers: 'rogers',
      bell: 'bell',
      telus: 'telus'
    };
    const assetName = supportedLogos[this.normalizeCarrierValue(carrier)];
    return assetName ? `assets/icons/carriers/${assetName}.svg` : '';
  }

  private normalizeCarrierValue(carrier: any): string {
    const value = String(carrier || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const aliases: { [key: string]: string } = {
      vzw: 'verizon',
      verizonwireless: 'verizon',
      tmo: 'tmobile',
      tmus: 'tmobile',
      atandt: 'att',
      cingular: 'att'
    };
    return aliases[value] || value;
  }

  reviewLteCarrierConnection(): void {
    if (!this.lteCarrierConnectTool || !this.lteCarrierConnectTool.available || !this.lteCarrierChangePending) return;
    this.previewSupportOperations(
      [this.operationForTool(this.lteCarrierConnectTool)],
      `Connect 4GLTE using ${this.selectedLteCarrierLabel}`
    );
  }

  get selectedLteCarrierLabel(): string {
    const option = this.lteCarrierOptions.find(item => item.value === this.selectedLteCarrier);
    return option ? option.label : '';
  }

  get lteCarrierChangePending(): boolean {
    return !!this.selectedLteCarrier && this.selectedLteCarrier !== this.currentLteCarrierValue;
  }

  toolIcon(toolId: string): string {
    const icons: any = {
      qos_reset: 'tune',
      sdwan_restart: 'sync',
      firewall_reload: 'security',
      reboot_device: 'power_settings_new',
      download_archive: 'archive',
      simulate_failover: 'swap_horiz',
      lte_safeguard: 'verified_user',
      carrier_switch: 'network_check'
    };
    return icons[toolId] || 'build';
  }

  reviewLanJump(): void {
    if (!this.lanJumpTool || !this.lanJumpTool.available || !this.canReviewLanJump) return;
    this.previewSupportOperations([{
      id: 'lanjump',
      parameters: { ...this.lanJump }
    }], 'Review LANJump configuration');
  }

  testLanJumpTarget(): void {
    if (!this.canTestLanJump) return;
    const testedSignature = this.currentLanJumpTestSignature();
    this.lanJumpTestState = 'testing';
    this.lanJumpTestMessage = `Testing ${this.lanJump.protocol.toUpperCase()} access to ${this.lanJump.target}:${this.lanJump.port}...`;
    this.lanJumpTestOutput = '';
    if (this.lanJumpTestSubscription) this.lanJumpTestSubscription.unsubscribe();
    this.lanJumpTestSubscription = this.apService.executeDeviceSupportTools(this.deviceMac, {
      operations: [{
        id: 'lanjump_test',
        parameters: {
          protocol: this.lanJump.protocol,
          target: String(this.lanJump.target || '').trim(),
          port: Number(this.lanJump.port),
          slot: Number(this.lanJump.slot)
        }
      }]
    }).subscribe(
      response => {
        if (testedSignature !== this.currentLanJumpTestSignature()) {
          this.resetLanJumpTest();
          return;
        }
        this.lanJumpTestState = 'passed';
        this.lanJumpTestSignature = testedSignature;
        this.lanJumpTestMessage = 'Connection test passed. Review the settings before enabling access.';
        this.applyLanJumpLiveState(response && response.output, this.lanJump);
        const testOutput = stripLanJumpLiveState(response && response.output, Number(this.lanJump.slot));
        this.lanJumpTestOutput = formatDiagnosticCliOutput(testOutput || 'Destination responded successfully.');
        this.lanJump.enabled = true;
      },
      error => {
        const body = error && error.error ? error.error : {};
        this.lanJumpTestState = 'failed';
        this.lanJumpTestSignature = '';
        this.lanJumpTestMessage = 'Connection test failed. Verify the destination, protocol and port, then try again.';
        this.lanJumpTestOutput = formatDiagnosticCliOutput(body.output || body.message || 'The LAN device did not respond.');
      }
    );
  }

  discoverLanJumpDevices(): void {
    if (!this.lanJumpDiscoveryTool || !this.lanJumpDiscoveryTool.available ||
      this.lanJumpDiscoveryState === 'loading' || this.running || !this.deviceMac) return;
    this.lanJumpDiscoveryState = 'loading';
    this.lanJumpDiscoveryMessage = 'Reading devices recently seen by the router...';
    if (this.lanJumpDiscoverySubscription) this.lanJumpDiscoverySubscription.unsubscribe();
    this.lanJumpDiscoverySubscription = this.apService.executeDeviceSupportTools(this.deviceMac, {
      operations: [{ id: 'ipv4_neighbours', parameters: {} }]
    }).subscribe(
      response => {
        this.lanJumpDestinations = enrichLanJumpDestinations(
          parseArpDestinations(response && response.output),
          this.lanJumpEnrichment
        );
        this.lanJumpDiscoveryState = 'loaded';
        this.updateLanJumpDiscoveryMessage();
      },
      error => {
        const body = error && error.error ? error.error : {};
        this.lanJumpDestinations = enrichLanJumpDestinations(
          parseArpDestinations(body.output),
          this.lanJumpEnrichment
        );
        this.lanJumpDiscoveryState = this.lanJumpDestinations.length ? 'loaded' : 'failed';
        this.lanJumpDiscoveryMessage = this.lanJumpDestinations.length
          ? `${this.lanJumpDestinations.length} LAN device${this.lanJumpDestinations.length === 1 ? '' : 's'} found in the available ARP output.`
          : 'The router did not return a recent LAN device list. Enter the destination IPv4 address manually or refresh the list.';
      }
    );
  }

  lanJumpDestinationLabel(destination: LanJumpDestination): string {
    const identity = destination.sip_name
      ? `${destination.sip_name}${destination.sip_user_id ? ` (${destination.sip_user_id})` : ''}`
      : '';
    return [destination.ip, identity, destination.hostname || (!identity ? destination.mac : '')]
      .filter(Boolean)
      .join(' — ');
  }

  filteredLanJumpDestinations(query: string): LanJumpDestination[] {
    const normalizedQuery = String(query || '').trim().toLowerCase();
    const selectedDestination = this.lanJumpDestinations.some(destination =>
      destination.ip.toLowerCase() === normalizedQuery
    );
    if (!normalizedQuery || selectedDestination) return this.lanJumpDestinations;

    return this.lanJumpDestinations.filter(destination => [
      destination.ip,
      destination.mac,
      destination.hostname,
      destination.sip_name,
      destination.sip_user_id,
      destination.interfaceName
    ].some(value => String(value || '').toLowerCase().includes(normalizedQuery)));
  }

  lanJumpDestinationTitle(destination: LanJumpDestination): string {
    return destination.sip_name || destination.hostname || destination.ip;
  }

  lanJumpDestinationDetails(destination: LanJumpDestination): string {
    const sipExtension = destination.sip_user_id ? `SIP ${destination.sip_user_id}` : '';
    return [destination.ip, sipExtension, destination.mac].filter(Boolean).join(' · ');
  }

  selectLanJumpDestination(ip: string): void {
    const destination = this.lanJumpDestinations.find(item => item.ip === ip);
    this.setLanJumpTarget(ip || '');
    if (destination && !String(this.lanJump.description || '').trim()) {
      this.lanJump.description = destination.sip_name || destination.hostname || `LAN Device ${this.lanJump.slot}`;
    }
  }

  chooseLanJumpDestination(session: any, ip: string): void {
    if (!ip) return;
    this.selectLanJumpSession(session);
    this.selectLanJumpDestination(ip);
  }

  selectLanJumpSession(session: any): void {
    if (!session || this.lanJump === session) return;
    this.lanJump = session;
    this.resetLanJumpTest('Select Test LAN Device to check this jump session.');
  }

  updateLanJumpTarget(session: any, target: string): void {
    this.selectLanJumpSession(session);
    this.setLanJumpTarget(target);
  }

  updateLanJumpProtocol(session: any, protocol: string): void {
    this.selectLanJumpSession(session);
    this.setLanJumpProtocol(protocol);
  }

  updateLanJumpPort(session: any, port: number): void {
    this.selectLanJumpSession(session);
    this.setLanJumpPort(port);
  }

  updateLanJumpExpiration(session: any, expiration: number): void {
    this.selectLanJumpSession(session);
    this.lanJump.expiration = Number(expiration);
  }

  updateLanJumpEnabled(session: any, enabled: boolean): void {
    this.selectLanJumpSession(session);
    session.enabled = Boolean(enabled);
  }

  lanJumpSessionTestStatus(session: any): string {
    if (this.lanJump !== session) return 'Not tested';
    if (this.lanJumpTestState === 'testing') return 'Testing';
    if (this.lanJumpTestState === 'passed') return 'Passed';
    if (this.lanJumpTestState === 'failed') return 'Failed';
    return 'Not tested';
  }

  lanJumpSessionStatus(session: any): string {
    const remaining = this.lanJumpRemainingSeconds(session);
    if (session && session.activeOnDevice && !session.appliedAtMs) return 'Active';
    return remaining > 0 ? `Active - ${this.formatRemainingTime(remaining)} remaining` : 'Inactive';
  }

  get lanJumpOperationInProgress(): boolean {
    return Boolean(this.suppressExecutionOutput && (this.running || this.polling) &&
      this.activeSupportOperations.some(operation => operation.id === 'lanjump'));
  }

  get lanJumpInlineStatusState(): string {
    if (this.lanJumpOperationInProgress) return 'applying';
    if (this.lanJump && this.lanJump.activeOnDevice) return 'active';
    return this.lanJumpTestState;
  }

  get lanJumpInlineStatusMessage(): string {
    if (this.lanJumpOperationInProgress) {
      return this.lanJump.enabled
        ? 'Enabling temporary LAN access. Waiting for confirmation from the device...'
        : 'Disabling temporary LAN access. Waiting for confirmation from the device...';
    }
    const currentStatus = `Current status: ${this.lanJumpSessionStatus(this.lanJump)}.`;
    if (this.lanJumpTestState === 'passed') return `${this.lanJumpTestMessage} ${currentStatus}`;
    if (this.lanJumpTestState === 'failed' || this.lanJumpTestState === 'testing') return this.lanJumpTestMessage;
    return currentStatus;
  }

  onCommandTabChanged(event: any): void {
    if (event && event.tab && event.tab.textLabel === 'LAN Jump' && this.lanJumpDiscoveryState === 'idle') {
      this.discoverLanJumpDevices();
    }
  }

  setLanJumpSlot(slot: number): void {
    this.lanJump.slot = Number(slot);
    this.onLanJumpConnectionChanged();
  }

  setLanJumpProtocol(protocol: string): void {
    this.lanJump.protocol = protocol;
    this.lanJump.port = protocol === 'http' ? 80 : 443;
    this.onLanJumpConnectionChanged();
  }

  setLanJumpTarget(target: string): void {
    this.lanJump.target = target;
    this.onLanJumpConnectionChanged();
  }

  setLanJumpPort(port: number): void {
    this.lanJump.port = Number(port);
    this.onLanJumpConnectionChanged();
  }

  onLanJumpConnectionChanged(): void {
    if (this.lanJumpTestState !== 'idle' || this.lanJumpTestSignature) this.resetLanJumpTest();
  }

  get canTestLanJump(): boolean {
    return Boolean(this.lanJumpTestTool && this.lanJumpTestTool.available && this.deviceMac &&
      !this.running && !this.reviewLoading && this.lanJumpTestState !== 'testing' && this.lanJumpConnectionValid);
  }

  get canReviewLanJump(): boolean {
    if (!this.lanJumpTool || !this.lanJumpTool.available || this.running || this.reviewLoading) return false;
    if (!this.lanJump.enabled) return Boolean(this.lanJump.activeOnDevice || this.lanJumpRemainingSeconds(this.lanJump) > 0);
    return Boolean(this.lanJumpTestState === 'passed' &&
      this.lanJumpTestSignature === this.currentLanJumpTestSignature() && this.lanJumpConfigurationValid);
  }

  get lanJumpReviewHint(): string {
    if (!this.lanJump.enabled) {
      return this.canReviewLanJump ? 'Ready to review and disable temporary access.' : 'This jump session is already inactive.';
    }
    if (!this.lanJumpConnectionValid) return 'Enter a valid IPv4 destination, protocol and port.';
    if (this.lanJumpTestState === 'testing') return 'Wait for the destination test to finish.';
    if (this.lanJumpTestState !== 'passed' ||
      this.lanJumpTestSignature !== this.currentLanJumpTestSignature()) {
      return 'Test this destination successfully before reviewing temporary access.';
    }
    if (!String(this.lanJump.description || '').trim()) return 'Add a short description so support can identify this jump.';
    if (!this.lanJumpConfigurationValid) return 'Check the description and access duration.';
    return 'Ready to review and enable.';
  }

  get lanJumpConnectionValid(): boolean {
    const target = String(this.lanJump.target || '').trim();
    const parts = target.split('.');
    const validTarget = parts.length === 4 && parts.every(part => /^\d{1,3}$/.test(part) && Number(part) <= 255);
    const port = Number(this.lanJump.port);
    return validTarget && Number.isInteger(port) && port >= 1 && port <= 65535 &&
      ['http', 'https'].indexOf(this.lanJump.protocol) >= 0 && [1, 2].indexOf(Number(this.lanJump.slot)) >= 0;
  }

  get lanJumpConfigurationValid(): boolean {
    const description = String(this.lanJump.description || '').trim();
    return this.lanJumpConnectionValid && /^[A-Za-z0-9 _.:-]{1,40}$/.test(description) &&
      [15, 30, 60, 120].indexOf(Number(this.lanJump.expiration)) >= 0;
  }

  private currentLanJumpTestSignature(): string {
    return [
      this.lanJump.protocol,
      String(this.lanJump.target || '').trim(),
      Number(this.lanJump.port)
    ].join('|');
  }

  private resetLanJumpTest(message = 'Destination changed. Run the connection test again before enabling access.'): void {
    this.lanJumpTestState = 'idle';
    this.lanJumpTestSignature = '';
    this.lanJumpTestOutput = '';
    this.lanJumpTestMessage = message;
  }

  toggleTechnical(tool: SupportTool, event: Event): void {
    event.stopPropagation();
    tool.technicalOpen = !tool.technicalOpen;
  }

  commandSummary(tool: SupportTool): string {
    const target = this.normalizedTarget() || '<destination>';
    const selected = this.selectedInterfaceDetails;
    const interfaceName = selected && selected.name || '<interface>';
    const sourceAddress = selected && selected.address || '<source IPv4>';
    const speedType: any = { auto: '', cellular: '1', narrowband: '2', broadband: '3', fiber: '4' };
    const speedParts = ['speedtest'];
    if (selected && selected.link) speedParts.push('--link-name', selected.link);
    speedParts.push('--interface', interfaceName);
    if (this.speedMode !== 'both') speedParts.push(`--${this.speedMode}`);
    if (speedType[this.speedConnectionType]) speedParts.push('--type', speedType[this.speedConnectionType]);
    speedParts.push('--output', 'text', '--share');
    const map: any = {
      link_flaps: 'sh /etc/showlinkflap.sh', link_statistics: 'sh /etc/showdpinger.sh',
      cellular_statistics: 'sh /usr/bin/get4g.sh',
      interface_statistics: `sh /etc/showifstat.sh '${this.ifstatGroup}' (capture ${this.ifstatDuration}s; stop an existing ${this.ifstatGroup} capture first)`,
      qos_statistics: 'sh /etc/showqos.sh', routing_table: 'ip route show [table]',
      ipv4_neighbours: 'cat /proc/net/arp', system_state: 'uptime; free -m', modem_version: 'atcmd.sh "ATI"',
      modem_firmware_memory: 'Read MemAvailable and verify at least 110 MB is free',
      carrier_scan: '4glte_scan.sh', speed_test: speedParts.join(' '),
      mtr: `mtr -4 -c ${Number(this.mtrCount)} -r -w ${this.mtrResolveNames ? '-b' : '-n'} -i ${Number(this.mtrInterval)} -a ${sourceAddress} ${target}`,
      ping: `ping -c 4 ${target}`,
      dns_lookup: `nslookup ${target}`, overlay_reachability: `ping -I ${interfaceName} -c 4 ${target}`,
      qos_reset: 'sh /etc/sdwanqos.sh reset', sdwan_restart: 'service sdwan restart',
      firewall_reload: 'fw3 -q reload', reboot_device: 'reboot', cellular_off: 'ifdown 4GLTE',
      lte_sim_read: '/usr/bin/4glte_config.sh sim read',
      lte_modem_off: '/usr/bin/4glte_config.sh off', lte_sim_unload: '/usr/bin/4glte_config.sh sim unload',
      lte_modem_reset: '/usr/bin/4glte_reset.sh', lte_sim_load: '/usr/bin/4glte_config.sh sim load',
      lte_carrier_connect: this.selectedLteCarrier
        ? `uci set network.4GLTE.carrier='${this.selectedLteCarrier}'; uci commit network; ifup 4GLTE; capture connection logs for 20s`
        : 'Select Verizon, T-Mobile, or AT&T to generate the connection commands'
    };
    return map[tool.id] || (tool.backend_action ? `Backend mapping required: ${tool.backend_action}` : 'Generated after validation');
  }

  onCommandsChanged(): void {
    const parsed = this.parseCommands(this.rawCommands);
    this.validCommands = parsed.valid;
    this.rejectedCommands = parsed.rejected;
  }

  clearCommands(): void {
    if (this.running) return;
    this.rawCommands = '';
    this.validCommands = [];
    this.rejectedCommands = [];
  }

  reviewConfigCommands(): void {
    if (!this.canUseAdvancedCommands) return;
    this.onCommandsChanged();
    if (!this.validCommands.length) return;
    this.reviewType = 'advanced';
    this.reviewTitle = 'Review advanced CLI commands';
    this.reviewCommands = this.validCommands.slice();
    this.reviewRejected = this.rejectedCommands.slice();
    this.reviewRisk = 'high';
    this.openReviewDialog();
  }

  confirmExecution(): void {
    if (!this.canProceedReview) return;
    if (this.reviewDialogRef) this.reviewDialogRef.close();
    if (this.reviewType === 'advanced') {
      if (!this.canUseAdvancedCommands) return;
      this.executeCommands(this.reviewCommands.slice(), this.reviewTitle);
      return;
    }
    this.executeSupportOperations({ operations: this.reviewOperations }, this.reviewTitle);
  }

  cancelReview(): void {
    if (this.reviewDialogRef) this.reviewDialogRef.close();
  }

  rerun(item: RunHistoryItem): void {
    this.suppressExecutionOutput = false;
    this.output = item.output;
    this.diagnosticOutput = item.diagnosticOutput;
    this.activeTab = this.runHistoryTabIndex;
    this.outputTabIndex = this.runHistoryTabIndex;
  }

  copyTicketSummary(): void {
    const summary = [
      `Device: ${this.deviceName} (${this.deviceMac})`,
      `Location: ${this.deviceLocation}`,
      `Run time: ${new Date().toISOString()}`,
      `Workflow: ${this.activeRunLabel || 'Device support tools'}`,
      `Result: ${this.executionStatusText}`,
      '',
      'Output summary:',
      this.displayOutput.slice(0, 4000)
    ].join('\n');
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(summary);
  }

  exportOutput(): void {
    const blob = new Blob([this.displayOutput], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `device-support-${this.deviceMac.replace(/:/g, '')}-${Date.now()}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  ngOnDestroy(): void {
    this.stopProgress();
    this.stopStatusPolling();
    this.stopLanJumpCountdown();
    [this.executionSubscription, this.statusSubscription, this.catalogSubscription, this.previewSubscription,
      this.lanJumpTestSubscription, this.lanJumpDiscoverySubscription]
      .filter(Boolean).forEach(subscription => subscription.unsubscribe());
  }

  private loadCatalog(preserveContent = false): void {
    if (!this.deviceMac) {
      this.catalogLoading = false;
      this.catalogError = 'Waiting for device information.';
      return;
    }
    this.catalogDeviceMac = this.deviceMac;
    this.catalogLoading = !preserveContent;
    this.catalogRefreshing = preserveContent;
    this.catalogError = '';
    const selectedToolIds = this.tools.filter(item => item.selected).map(item => item.id);
    if (!preserveContent) {
      this.tools = [];
      this.supportInterfaces = [];
      this.selectedInterface = '';
    }
    if (this.catalogSubscription) this.catalogSubscription.unsubscribe();
    this.catalogSubscription = this.apService.getDeviceSupportTools(this.deviceMac).subscribe(
      response => {
        this.tools = (response.tools || []).map((item: SupportTool) => ({
          ...item,
          selected: preserveContent && selectedToolIds.indexOf(item.id) >= 0
        }));
        this.supportInterfaces = response.interfaces || [];
        this.applyCarrierMarket(response.carrier_market);
        this.applyLanJumpDiscovery(response.lan_jump_discovery);
        const snapshot = response.diagnostics_snapshot || {};
        this.diagnosticsRows = snapshot.rows || [];
        this.diagnosticsTotalUsageBytes = Number(snapshot.total_usage_bytes || 0);
        this.diagnosticsCapturedAt = snapshot.captured_at || null;
        this.diagnosticsSnapshotStale = snapshot.stale !== false;
        this.diagnosticsTarget = response.default_target || this.diagnosticsTarget;
        const selectedInterfaceExists = this.supportInterfaces.some(item => item.name === this.selectedInterface);
        if (!selectedInterfaceExists) {
          const onlineInterface = this.supportInterfaces.find(item => item.state) || this.supportInterfaces[0];
          this.selectedInterface = onlineInterface ? onlineInterface.name : '';
        }
        this.initializeConfigurationValues();
        this.initializeLanJumpSessions();
        this.refreshToolCollections();
        this.catalogLoading = false;
        this.catalogRefreshing = false;
        if (preserveContent) this.refreshSelectionState();
        else this.activateWorkflow(this.selectedWorkflow);
      },
      error => {
        this.catalogLoading = false;
        this.catalogRefreshing = false;
        this.catalogError = error && error.name === 'TimeoutError'
          ? 'Support tools request timed out. Check the Device Service and retry.'
          : error && error.error && error.error.message || 'Unable to load support tools.';
      }
    );
  }

  private applyLanJumpDiscovery(discovery: any): void {
    this.lanJumpDiscoverySource = discovery && discovery.source === 'hybrid' ? 'hybrid' : 'shell';
    this.lanJumpEnrichment = this.lanJumpDiscoverySource === 'hybrid' && Array.isArray(discovery.enrichment)
      ? discovery.enrichment
      : [];
    this.lanJumpDestinations = [];
    this.lanJumpDiscoveryState = 'idle';
    this.lanJumpDiscoveryMessage = 'Discover devices recently seen on the LAN, or enter an IPv4 address manually.';
  }

  private updateLanJumpDiscoveryMessage(): void {
    this.lanJumpDiscoveryMessage = this.lanJumpDestinations.length
      ? `${this.lanJumpDestinations.length} LAN device${this.lanJumpDestinations.length === 1 ? '' : 's'} found. Select one below or enter another IPv4 address.`
      : 'No recent LAN devices were found. Enter the destination IPv4 address manually.';
  }

  private initializeConfigurationValues(): void {
    const teamConfig = this.currentDevice.templateConfig && this.currentDevice.templateConfig.team &&
      this.currentDevice.templateConfig.team.config || {};
    const teamName = Object.keys(teamConfig)[0];
    const link2 = teamName && teamConfig[teamName] && teamConfig[teamName].link2 || {};
    this.currentConfigurationValues = {
      lte_safeguard: String(link2.qos_safeguard_enabled || '0') === '1',
      carrier_switch: String(link2.monitor_carrier_switch || '0') === '1'
    };
    this.configurationValues = { ...this.currentConfigurationValues };
  }

  private initializeLanJumpSessions(): void {
    const jumpConfig = this.currentDevice.templateConfig && this.currentDevice.templateConfig.jump ||
      this.currentDevice.jump || {};
    this.lanJumpSessions = [1, 2].map(slot => {
      const config = jumpConfig[`landevice${slot}`] || {};
      const protocol = String(config.proto || 'https').toLowerCase() === 'http' ? 'http' : 'https';
      const expiration = Number(config.expire_time);
      const appliedAtSeconds = Number(config.applied_time);
      const enabled = Number(config.enabled) === 1 && String(config.status || '').toLowerCase() !== 'inactive';
      return {
        description: config.description || config.name || `LANDevice${slot}`,
        protocol,
        port: Number(config.localport) || (protocol === 'http' ? 80 : 443),
        target: config.forward_host || '',
        expiration: [15, 30, 60, 120].indexOf(expiration) >= 0 ? expiration : (slot === 1 ? 120 : 15),
        slot,
        status: enabled ? 'active' : 'inactive',
        enabled,
        activeOnDevice: enabled,
        activeExpiration: expiration,
        appliedAtMs: Number.isFinite(appliedAtSeconds) && appliedAtSeconds > 0 ? appliedAtSeconds * 1000 : 0
      };
    });
    this.lanJump = this.lanJumpSessions[0];
    this.startLanJumpCountdown();
    this.resetLanJumpTest('Select Test LAN Device to check the selected jump session.');
  }

  private applyCompletedSupportState(): void {
    const operation = this.activeSupportOperations.find(item => item.id === 'lanjump');
    if (!operation) return;
    const parameters: any = operation.parameters || {};
    const session = this.lanJumpSessions.find(item => Number(item.slot) === Number(parameters.slot));
    if (!session) return;

    if (parameters.enabled === false) {
      session.enabled = false;
      session.activeOnDevice = false;
      session.status = 'inactive';
      session.appliedAtMs = 0;
    } else {
      Object.assign(session, {
        description: parameters.description,
        protocol: parameters.protocol,
        port: Number(parameters.port),
        target: parameters.target,
        expiration: Number(parameters.expiration),
        enabled: true,
        activeOnDevice: true,
        status: 'active',
        activeExpiration: Number(parameters.expiration),
        appliedAtMs: Date.now()
      });
    }
    this.startLanJumpCountdown();
  }

  lanJumpRemainingSeconds(session: any): number {
    if (!session || !session.activeOnDevice || !session.appliedAtMs) return 0;
    const expiration = Number(session.activeExpiration || session.expiration);
    const expiresAt = Number(session.appliedAtMs) + expiration * 60 * 1000;
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
  }

  private applyLanJumpLiveState(output: any, session: any): void {
    const liveState = parseLanJumpLiveState(output, Number(session.slot));
    if (!liveState) return;
    session.activeOnDevice = liveState.enabled;
    session.status = liveState.status;
    session.appliedAtMs = liveState.appliedAtMs;
    session.activeExpiration = liveState.expiration || Number(session.expiration);
    if (liveState.enabled) session.enabled = true;
    this.startLanJumpCountdown();
  }

  private formatRemainingTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  private updateLanJumpExpirationState(): void {
    this.lanJumpSessions.forEach(session => {
      if (session.activeOnDevice && session.appliedAtMs && this.lanJumpRemainingSeconds(session) <= 0) {
        session.enabled = false;
        session.activeOnDevice = false;
        session.status = 'inactive';
      }
    });
  }

  private startLanJumpCountdown(): void {
    this.stopLanJumpCountdown();
    this.updateLanJumpExpirationState();
    if (this.lanJumpSessions.some(session => session.activeOnDevice)) {
      this.lanJumpCountdownTimer = setInterval(() => this.updateLanJumpExpirationState(), 1000);
    }
  }

  private stopLanJumpCountdown(): void {
    if (this.lanJumpCountdownTimer) {
      clearInterval(this.lanJumpCountdownTimer);
      this.lanJumpCountdownTimer = null;
    }
  }

  private refreshToolCollections(): void {
    const serviceActions = this.tools.filter(item => ['download_archive', 'simulate_failover'].indexOf(item.id) < 0);
    this.lowImpactActions = serviceActions.filter(item => item.kind === 'service' && item.category === 'Low impact');
    this.highImpactActions = serviceActions.filter(item => item.kind === 'service' && item.category === 'High impact');
    const routineIds = ['qos_reset', 'sdwan_restart'];
    const highImpactIds = ['firewall_reload', 'reboot_device'];
    this.networkRoutineActions = routineIds.map(id => this.tools.find(item => item.id === id)).filter(Boolean);
    this.networkHighImpactActions = highImpactIds.map(id => this.tools.find(item => item.id === id)).filter(Boolean);
    this.configurationTools = this.tools.filter(item => item.kind === 'configuration' && item.id !== 'lanjump');
    this.lteConfigurationTools = this.configurationTools.filter(item => ['lte_safeguard', 'carrier_switch'].indexOf(item.id) >= 0);
    this.lanJumpTool = this.tools.find(item => item.id === 'lanjump');
    this.lanJumpTestTool = this.tools.find(item => item.id === 'lanjump_test');
    this.lanJumpDiscoveryTool = this.tools.find(item => item.id === 'ipv4_neighbours');
    this.lteCarrierScanTool = this.tools.find(item => item.id === 'carrier_scan');
    this.lteSimReadTool = this.tools.find(item => item.id === 'lte_sim_read');
    this.lteModemVersionTool = this.tools.find(item => item.id === 'modem_version');
    this.lteFirmwareMemoryTool = this.tools.find(item => item.id === 'modem_firmware_memory');
    this.lteModemOffTool = this.tools.find(item => item.id === 'lte_modem_off');
    this.lteSimUnloadTool = this.tools.find(item => item.id === 'lte_sim_unload');
    this.lteModemResetTool = this.tools.find(item => item.id === 'lte_modem_reset');
    this.lteSimLoadTool = this.tools.find(item => item.id === 'lte_sim_load');
    this.lteCarrierConnectTool = this.tools.find(item => item.id === 'lte_carrier_connect');
    this.refreshDiagnosticGroups();
    this.refreshSelectionState();
  }

  refreshSelectionState(): void {
    this.selectedDiagnostics = this.tools.filter(item => item.kind === 'diagnostic' && item.selected);
    this.selectedDiagnosticCount = this.selectedDiagnostics.length;
    this.selectedEstimatedDuration = this.selectedDiagnostics.reduce(
      (total, item) => total + Number(item.expected_duration_seconds || 0),
      0
    );
    this.speedTestSelected = this.selectedDiagnostics.some(item => item.id === 'speed_test');
    this.mtrSelected = this.selectedDiagnostics.some(item => item.id === 'mtr');
  }

  private operationForTool(tool: SupportTool): SupportOperation {
    const parameters: any = {};
    if (tool.required_parameters.indexOf('target') >= 0) parameters.target = this.normalizedTarget();
    if (tool.required_parameters.indexOf('interface') >= 0) parameters.interface = this.selectedInterface;
    if (tool.id === 'speed_test') {
      parameters.mode = this.speedMode;
      parameters.connection_type = this.speedConnectionType;
      parameters.share = this.speedShare;
    }
    if (tool.id === 'mtr') {
      parameters.count = Number(this.mtrCount);
      parameters.interval = Number(this.mtrInterval);
      parameters.resolve_names = this.mtrResolveNames;
    }
    if (tool.id === 'interface_statistics') {
      parameters.interface_group = this.ifstatGroup;
      parameters.duration_seconds = Number(this.ifstatDuration);
    }
    if (tool.id === 'lte_carrier_connect') parameters.carrier = this.selectedLteCarrier;
    else if (tool.kind === 'configuration') parameters.enabled = Boolean(this.configurationValues[tool.id]);
    return { id: tool.id, parameters };
  }

  private isSdwanInterface(item: SupportInterface): boolean {
    const name = String(item && item.name || '').toLowerCase();
    const type = String(item && item.type || '').toLowerCase();
    return Boolean(item && item.link) || ['underlay', 'overlay'].indexOf(type) >= 0 ||
      /^(?:wan\d*|wwan\d*|wg\d+|rrt\d+)$/.test(name);
  }

  private isLanInterface(item: SupportInterface): boolean {
    const name = String(item && item.name || '').toLowerCase();
    return !this.isSdwanInterface(item) && /^(?:br-)?(?:lan\d*|guest\d*)$/.test(name);
  }

  private previewSupportOperations(operations: SupportOperation[], title: string): void {
    if (!this.deviceMac || this.reviewLoading) return;
    this.reviewLoading = true;
    this.reviewError = '';
    this.previewSubscription = this.apService.previewDeviceSupportTools(this.deviceMac, { operations }).subscribe(
      response => {
        this.reviewLoading = false;
        this.reviewType = 'support';
        this.reviewTitle = title;
        this.reviewOperations = operations;
        this.reviewCommands = response.commands || [];
        this.reviewRejected = [];
        this.reviewDurationSeconds = response.estimated_duration_seconds || 0;
        this.reviewRisk = response.risk_level || 'read-only';
        this.openReviewDialog();
      },
      error => {
        this.reviewLoading = false;
        this.reviewError = error && error.name === 'TimeoutError'
          ? 'Support tool preview timed out. Check the Device Service and retry.'
          : error && error.error && error.error.message || 'Unable to prepare these support tools.';
      }
    );
  }

  private openReviewDialog(): void {
    this.reviewDialogRef = this.dialog.open(this.commandReviewDialog, {
      width: '760px', maxWidth: '96vw', maxHeight: '92vh', disableClose: true,
      panelClass: 'device-command-review-dialog'
    });
  }

  private executeSupportOperations(payload: any, label: string): void {
    this.activeSupportOperations = (payload.operations || []).map(operation => ({
      id: operation.id,
      parameters: { ...(operation.parameters || {}) }
    }));
    this.suppressExecutionOutput = this.activeSupportOperations.some(operation => operation.id === 'lanjump');
    this.diagnosticOutput = (payload.operations || []).some(operation => {
      const tool = this.tools.find(item => item.id === operation.id);
      return tool && tool.kind === 'diagnostic';
    });
    this.activeRunExpectedSeconds = this.diagnosticOutput ? this.reviewDurationSeconds : 0;
    this.beginExecution(label);
    this.executionSubscription = this.apService.executeDeviceSupportTools(this.deviceMac, payload).subscribe(
      response => this.handleExecutionResponse(response),
      error => this.handleExecutionError(error)
    );
  }

  private executeCommands(commands: string[], label: string): void {
    this.activeSupportOperations = [];
    this.suppressExecutionOutput = false;
    this.diagnosticOutput = false;
    this.activeRunExpectedSeconds = 0;
    this.beginExecution(label);
    this.executionSubscription = this.apService.executeDeviceShellOperations(
      this.deviceMac,
      commands,
      this.requiresAsyncExecution(commands) ? 'async' : 'auto'
    ).subscribe(
      response => this.handleExecutionResponse(response),
      error => this.handleExecutionError(error)
    );
  }

  private beginExecution(label: string): void {
    this.stopStatusPolling();
    this.outputTabIndex = this.activeTab;
    this.running = true;
    this.executionState = 'running';
    this.elapsedSeconds = 0;
    this.output = '';
    this.responseMeta = null;
    this.activeRunLabel = label;
    this.activeRunStartedAt = Date.now();
    this.progressTimer = setInterval(() => this.elapsedSeconds += 1, 1000);
  }

  private handleExecutionResponse(response: any): void {
    this.stopProgress();
    this.responseMeta = response;
    if (response && response.execution_status === 'accepted' && response.operation_id) {
      this.executionState = 'accepted';
      this.setOutput(response.message, 'Operation accepted for background execution.');
      if (this.suppressExecutionOutput) this.toastr.info('Applying LAN Jump settings on the device...');
      this.startStatusPolling(response.operation_id, response.poll_after_ms);
      return;
    }
    this.setOutput(response && response.output, 'The operation completed without output.');
    this.executionState = 'completed';
    this.applyCompletedSupportState();
    this.notifyLanJumpCompletion();
    this.recordRun('Completed');
  }

  private handleExecutionError(error: any): void {
    this.stopProgress();
    const body = error && error.error ? error.error : {};
    this.responseMeta = body;
    this.setOutput(body.output || body.message, 'Unable to execute operations on the device.');
    this.executionState = 'failed';
    if (this.suppressExecutionOutput) {
      this.toastr.error(body.message || 'Unable to apply LAN Jump settings.');
    }
    this.recordRun('Failed');
  }

  private startStatusPolling(operationId: string, delayMs?: number): void {
    this.polling = true;
    this.scheduleStatusPoll(operationId, delayMs || 3000);
  }

  private scheduleStatusPoll(operationId: string, delayMs: number): void {
    if (!this.polling) return;
    this.statusPollTimer = setTimeout(() => {
      this.statusSubscription = this.apService.getDeviceShellOperation(this.deviceMac, operationId).subscribe(
        response => this.handleStatusResponse(operationId, response),
        error => this.handleStatusError(operationId, error)
      );
    }, Math.max(1000, delayMs));
  }

  private handleStatusResponse(operationId: string, response: any): void {
    this.responseMeta = response;
    const status = response && response.execution_status;
    if (status === 'completed') {
      this.polling = false;
      this.executionState = 'completed';
      this.setOutput(response.output, 'The operation completed without output.');
      this.applyCompletedSupportState();
      this.notifyLanJumpCompletion();
      this.recordRun('Completed');
      return;
    }
    if (status === 'failed') {
      this.polling = false;
      this.executionState = 'failed';
      this.setOutput(response.output || response.message, 'Operation execution failed.');
      if (this.suppressExecutionOutput) {
        this.toastr.error(response.message || 'Unable to apply LAN Jump settings.');
      }
      this.recordRun('Failed');
      return;
    }
    this.executionState = 'accepted';
    if (response && (response.output || response.message)) this.setOutput(response.output || response.message, '');
    this.continueStatusPolling(operationId, response && response.poll_after_ms);
  }

  private handleStatusError(operationId: string, error: any): void {
    const body = error && error.error ? error.error : {};
    if (error && error.status >= 500) {
      this.setOutput(body.message, 'The device is reconnecting. Completion is still being verified.');
      this.continueStatusPolling(operationId, 5000);
      return;
    }
    this.polling = false;
    this.executionState = 'failed';
    this.responseMeta = body;
    this.setOutput(body.output || body.message, 'Unable to verify operation execution.');
    if (this.suppressExecutionOutput) {
      this.toastr.error(body.message || 'Unable to verify the LAN Jump settings.');
    }
    this.recordRun('Failed');
  }

  private continueStatusPolling(operationId: string, delayMs?: number): void {
    this.scheduleStatusPoll(operationId, delayMs || 3000);
  }

  private recordRun(status: string): void {
    if (!this.activeRunStartedAt) return;
    this.runHistory.unshift({
      startedAt: new Date(this.activeRunStartedAt),
      label: this.activeRunLabel,
      status,
      runtimeMs: Date.now() - this.activeRunStartedAt,
      output: this.output,
      diagnosticOutput: this.diagnosticOutput
    });
    this.activeRunStartedAt = 0;
    this.activeSupportOperations = [];
  }

  private notifyLanJumpCompletion(): void {
    const operation = this.activeSupportOperations.find(item => item.id === 'lanjump');
    if (!operation) return;
    const disabled = operation.parameters && operation.parameters.enabled === false;
    this.toastr.success(disabled
      ? 'LAN Jump access has been disabled.'
      : 'LAN Jump access has been enabled for the selected duration.');
  }

  private stopStatusPolling(): void {
    this.polling = false;
    if (this.statusPollTimer) {
      clearTimeout(this.statusPollTimer);
      this.statusPollTimer = null;
    }
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
      this.statusSubscription = null;
    }
  }

  private requiresAsyncExecution(commands: string[]): boolean {
    return commands.some(command => /(^|[;\n]\s*)(service\s+sdwan\s+restart|\/etc\/init\.d\/sdwan\s+restart|reboot)(?=\s*(?:;|$))/m.test(
      String(command || '').trim()
    ));
  }

  private stopProgress(): void {
    this.running = false;
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  private setOutput(value: any, fallback: string): void {
    const hasValue = value !== null && value !== undefined && value !== '';
    const formatted = formatCliOutput(hasValue ? value : fallback);
    const suppressBenignSdwanWarnings = this.activeSupportOperations.some(operation =>
      operation.id === 'qos_reset' || operation.id === 'sdwan_restart'
    );
    if (suppressBenignSdwanWarnings) {
      this.output = stripBenignSdwanWarnings(formatted) || formatCliOutput(fallback);
      return;
    }
    this.output = formatted;
  }

  private speedNumber(value: any): number {
    const parsed = Number.parseFloat(String(value === undefined || value === null ? '' : value));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private normalizedTarget(): string {
    const value = String(this.diagnosticsTarget || '').trim();
    return /^[A-Za-z0-9.-]+$/.test(value) ? value : '';
  }

  private parseCommands(source: string): { valid: string[]; rejected: RejectedCommand[] } {
    const valid: string[] = [];
    const rejected: RejectedCommand[] = [];
    let activeBlock: { lines: string[]; startLine: number; terminators: string[] } = null;
    String(source || '').replace(/\r\n?/g, '\n').split('\n').forEach((line, index) => {
      const command = this.stripComment(line).trim();
      if (activeBlock) {
        if (command) activeBlock.lines.push(command);
        const nestedTerminator = command ? this.compoundTerminator(command) : '';
        if (nestedTerminator && !this.hasCompoundTerminator(command, nestedTerminator)) {
          activeBlock.terminators.push(nestedTerminator);
        } else if (command && this.hasCompoundTerminator(command, activeBlock.terminators[activeBlock.terminators.length - 1])) {
          activeBlock.terminators.pop();
        }
        if (!activeBlock.terminators.length) {
          this.addParsedCommand(activeBlock.lines.join('\n'), activeBlock.startLine, valid, rejected);
          activeBlock = null;
        }
        return;
      }
      if (!command) return;
      const terminator = this.compoundTerminator(command);
      if (terminator && !this.hasCompoundTerminator(command, terminator)) {
        activeBlock = { lines: [command], startLine: index + 1, terminators: [terminator] };
        return;
      }
      this.addParsedCommand(command, index + 1, valid, rejected);
    });
    if (activeBlock) {
      rejected.push({
        line: activeBlock.startLine,
        command: activeBlock.lines.join('\n'),
        reason: `Missing ${activeBlock.terminators[activeBlock.terminators.length - 1]} for compound command`
      });
    }
    return { valid, rejected };
  }

  private addParsedCommand(command: string, line: number, valid: string[], rejected: RejectedCommand[]): void {
    const reason = this.validateCommand(command);
    if (reason) rejected.push({ line, command, reason });
    else valid.push(command);
  }

  private compoundTerminator(command: string): string {
    const normalized = command.trim();
    if (/^(for|while|until)\b/.test(normalized)) return 'done';
    if (/^if\b/.test(normalized)) return 'fi';
    if (/^case\b/.test(normalized)) return 'esac';
    if (/^(?:function\s+)?[A-Za-z_][A-Za-z0-9_]*\s*\(\)\s*\{/.test(normalized)) return '}';
    if (/^\{\s*$/.test(normalized)) return '}';
    return '';
  }

  private hasCompoundTerminator(command: string, terminator: string): boolean {
    const withoutQuotedText = this.removeQuotedText(command).trim();
    const escapedTerminator = terminator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|[;\\s])${escapedTerminator}\\s*;?\\s*$`).test(withoutQuotedText);
  }

  private removeQuotedText(command: string): string {
    let result = '';
    let singleQuoted = false;
    let doubleQuoted = false;
    let escaped = false;
    for (const character of command) {
      if (escaped) {
        escaped = false;
        if (!singleQuoted && !doubleQuoted) result += character;
        continue;
      }
      if (character === '\\' && !singleQuoted) {
        escaped = true;
        if (!doubleQuoted) result += character;
        continue;
      }
      if (character === '\'' && !doubleQuoted) {
        singleQuoted = !singleQuoted;
        continue;
      }
      if (character === '"' && !singleQuoted) {
        doubleQuoted = !doubleQuoted;
        continue;
      }
      if (!singleQuoted && !doubleQuoted) result += character;
    }
    return result;
  }

  private validateCommand(command: string): string {
    if (command.indexOf('`') >= 0) return 'Backticks are not allowed';
    if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(command)) return 'Unsupported control character';
    if (command.length > 8192) return 'Command exceeds 8192 characters';
    if (this.hasUnbalancedQuotes(command)) return 'Unbalanced quotation mark';
    if (/\\$/.test(command)) return 'Incomplete line continuation';
    return '';
  }

  private stripComment(line: string): string {
    let singleQuoted = false;
    let doubleQuoted = false;
    let escaped = false;
    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];
      if (escaped) {
        escaped = false;
        continue;
      }
      if (character === '\\' && !singleQuoted) {
        escaped = true;
        continue;
      }
      if (character === '\'' && !doubleQuoted) singleQuoted = !singleQuoted;
      if (character === '"' && !singleQuoted) doubleQuoted = !doubleQuoted;
      if (character === '#' && !singleQuoted && !doubleQuoted && (index === 0 || /\s/.test(line[index - 1]))) {
        return line.slice(0, index);
      }
    }
    return line;
  }

  private hasUnbalancedQuotes(command: string): boolean {
    let singleQuoted = false;
    let doubleQuoted = false;
    let escaped = false;
    for (const character of command) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (character === '\\' && !singleQuoted) escaped = true;
      else if (character === '\'' && !doubleQuoted) singleQuoted = !singleQuoted;
      else if (character === '"' && !singleQuoted) doubleQuoted = !doubleQuoted;
    }
    return singleQuoted || doubleQuoted;
  }
}

export {
  formatCliOutput,
  formatDiagnosticCliOutput,
  linkifyCliOutput,
  normalizeTerminalJsonCandidate,
  parseArpDestinations,
  enrichLanJumpDestinations,
  parseLanJumpLiveState,
  stripBenignSdwanWarnings
};
